import { Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Attachment, IssueType, MIXPANEL_EVENTS } from 'vendor-marketplace-shared-library';
import React, { useRef } from 'react';
import PrimaryButton from '../../../components/common/styled-mui-overrides/PrimaryButton';
import ImageThumbnail from '../../../components/common/ImageThumbnail';
import SecondaryButton from '../../../components/common/styled-mui-overrides/SecondaryButton';
import { useNavigate } from 'react-router-dom';
import { UpdateWorkOrderFormData } from '../CreateWorkOrderFormPage';
import useMixpanel from '../../../hooks/useMixpanel';

type CreateWorkOrderWhatProps = {
  workOrderId: string;
  formData: Partial<UpdateWorkOrderFormData>;
  issueTypes: IssueType[];
  setFormData: (data: Partial<UpdateWorkOrderFormData>) => void;
  attachments: Attachment[];
  onUploadAttachment: (file: File) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onSubmit: () => void;
};
export default function CreateWorkOrderWhat(props: CreateWorkOrderWhatProps) {
  const navigate = useNavigate();
  const { mixpanelTrack } = useMixpanel();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_ADD_ATTACHMENT_ATTEMPTED);
    props.onUploadAttachment(file);
  };

  return (
    <form>
      <Grid container sx={{ justifyContent: 'center', paddingTop: 4 }} xs={12} spacing={2}>
        {/*Title input*/}
        <Grid item xs={0} md={3}></Grid>
        <Grid item container xs={12} md={6} spacing={2} sx={{ justifyContent: 'center' }}>
          {/*Title*/}
          <Grid item xs={12}>
            <InputLabel>Task title</InputLabel>
            <TextField
              value={props.formData.title}
              onChange={e => props.setFormData({ ...props.formData, title: e.target.value })}
              placeholder={'AC is broken...'}
              fullWidth
            />
          </Grid>

          {/*Issue type input*/}
          <Grid item xs={12}>
            <InputLabel>Issue Type</InputLabel>
            {props.issueTypes.length > 1 ? (
              <Select
                value={props.formData.issueTypeId ?? ''}
                placeholder={'Issue type'}
                onChange={e =>
                  props.setFormData({ ...props.formData, issueTypeId: Number(e.target.value) })
                }
                fullWidth
              >
                {props.issueTypes.map((issueType: IssueType) => (
                  <MenuItem key={issueType.id} value={issueType.id}>
                    {issueType.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography>{props.issueTypes[0]?.name ?? 'None'}</Typography>
            )}
          </Grid>

          {/*Description input*/}
          <Grid item xs={12}>
            <InputLabel>Description</InputLabel>
            <TextField
              placeholder={'AC is broken...'}
              onChange={e => props.setFormData({ ...props.formData, description: e.target.value })}
              rows={4}
              multiline
              value={props.formData.description}
              fullWidth
            />
          </Grid>

          {/*Attachments*/}
          {props.attachments && props.attachments?.length > 0 && (
            <Grid container item xs={12} spacing={2}>
              {props.attachments?.map(attachment => {
                if (!attachment) return;
                return (
                  <Grid item key={attachment.id}>
                    <ImageThumbnail
                      attachment={attachment}
                      onDelete={() => {
                        mixpanelTrack(
                          MIXPANEL_EVENTS.WORK_ORDER_UPDATE_DELETE_ATTACHMENT_ATTEMPTED,
                        );

                        props.onRemoveAttachment(attachment.id);
                      }}
                      deleteButtonStyles={{ color: 'black' }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/*Action buttons*/}
          <Grid item>
            <input hidden type={'file'} ref={inputRef} onChange={onChange} />
            <SecondaryButton onClick={onUploadClick}>Upload photo/video</SecondaryButton>
          </Grid>
          <Grid item>
            <PrimaryButton
              onClick={() => {
                props.onSubmit();
                navigate(`/create-work-order/${props.workOrderId}/?step=2`);
                mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_CREATE_DETAILS_CONFIRMED);
              }}
            >
              Next
            </PrimaryButton>
          </Grid>
        </Grid>
        <Grid item xs={0} md={3}></Grid>
      </Grid>
    </form>
  );
}
